import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronLeft, ChevronRight, Today } from "@mui/icons-material";
import { blue, grey, lightBlue } from "@mui/material/colors";
import dayjs, { Dayjs } from "dayjs";
import {
  getRFQStatusLabel,
  getRfqStatusColor,
  RFQStatus,
} from "../../models/RFQStatus";
import RfqStatusSinceTooltip from "./RfqStatusSinceTooltip";

export type WorkQueueCalendarDateField = "dueDate" | "receivedDate";

export interface WorkQueueCalendarItem {
  id: number;
  rfqNum: string;
  customerName: string;
  buyerName: string;
  dueDate: string;
  receivedDate: string;
  note: string | null;
  rfqStatus: RFQStatus | string;
  /** Extra chips (e.g. HOT + NEW) shown after the primary status. */
  extraStatuses?: string[];
}

interface WorkQueueCalendarProps {
  items: WorkQueueCalendarItem[];
  dateField: WorkQueueCalendarDateField;
  onOpenRfq: (rfqId: number) => void;
  onMoveRfq?: (rfqId: number, dateYmd: string) => void | Promise<void>;
  statusCatalog?: {
    value: string;
    label: string;
    colorBg?: string | null;
    colorFg?: string | null;
    colorBorder?: string | null;
  }[];
  highlightedRfqId?: number | null;
}

function parseYmdToDayjs(value: string): Dayjs | null {
  const trimmed = (value || "").trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const d = dayjs(trimmed.slice(0, 10));
    return d.isValid() ? d.startOf("day") : null;
  }
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(trimmed);
  if (us) {
    const d = dayjs(
      `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`
    );
    return d.isValid() ? d.startOf("day") : null;
  }
  const d = dayjs(trimmed);
  return d.isValid() ? d.startOf("day") : null;
}

function formatDisplayDate(value: string): string {
  const d = parseYmdToDayjs(value);
  return d ? d.format("M/D/YYYY") : value || "";
}

function buildMonthGridDays(focus: Dayjs): Dayjs[] {
  const start = focus.startOf("month");
  const end = focus.endOf("month");
  // Monday-start week
  const gridStart = start.day() === 0 ? start.subtract(6, "day") : start.subtract(start.day() - 1, "day");
  const gridEnd = end.day() === 0 ? end : end.add(7 - end.day(), "day");
  const days: Dayjs[] = [];
  let cursor = gridStart;
  while (cursor.isBefore(gridEnd) || cursor.isSame(gridEnd, "day")) {
    days.push(cursor);
    cursor = cursor.add(1, "day");
  }
  return days;
}

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function itemDateKey(
  item: WorkQueueCalendarItem,
  dateField: WorkQueueCalendarDateField
): string | null {
  const raw = dateField === "dueDate" ? item.dueDate : item.receivedDate;
  const d = parseYmdToDayjs(raw);
  return d ? d.format("YYYY-MM-DD") : null;
}

const WorkQueueCalendar: React.FC<WorkQueueCalendarProps> = ({
  items,
  dateField,
  onOpenRfq,
  onMoveRfq,
  statusCatalog,
  highlightedRfqId = null,
}) => {
  const [focusDate, setFocusDate] = useState(() => {
    if (highlightedRfqId != null) {
      const item = items.find((entry) => entry.id === highlightedRfqId);
      const key = item ? itemDateKey(item, dateField) : null;
      if (key) return dayjs(key).startOf("month");
    }
    return dayjs().startOf("month");
  });
  const [dragSourceId, setDragSourceId] = useState<number | null>(null);
  const [dropDayKey, setDropDayKey] = useState<string | null>(null);
  const dragSourceIdRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const highlightedItemRef = useRef<HTMLDivElement | null>(null);
  const canDrag = onMoveRfq != null;

  useEffect(() => {
    highlightedItemRef.current?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
  }, [highlightedRfqId, focusDate]);

  const byDateKey = useMemo(() => {
    const map = new Map<string, WorkQueueCalendarItem[]>();
    for (const item of items) {
      const key = itemDateKey(item, dateField);
      if (!key) continue;
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    for (const list of Array.from(map.values())) {
      list.sort((a, b) => a.rfqNum.localeCompare(b.rfqNum));
    }
    return map;
  }, [items, dateField]);

  const days = useMemo(() => buildMonthGridDays(focusDate), [focusDate]);
  const focusMonth = focusDate.month();

  return (
    <Stack
      gap={1}
      sx={{
        flex: 1,
        minHeight: 0,
        bgcolor: "white",
        border: `1px solid ${grey[400]}`,
        borderRadius: 1,
        p: 1.5,
        mb: 1,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => setFocusDate((d) => d.subtract(1, "month"))}
            aria-label="Previous month"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 160, textAlign: "center" }}>
            {focusDate.format("MMMM YYYY")}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setFocusDate((d) => d.add(1, "month"))}
            aria-label="Next month"
          >
            <ChevronRight />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setFocusDate(dayjs().startOf("month"))}
            aria-label="This month"
          >
            <Today fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Placed by {dateField === "dueDate" ? "RFQ due date" : "received date"}
          {canDrag ? " · drag an RFQ onto a day to reschedule" : ""}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 0.5,
        }}
      >
        {WEEKDAY_LABELS.map((label) => (
          <Typography
            key={label}
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", fontWeight: 600, py: 0.5 }}
          >
            {label}
          </Typography>
        ))}
        {days.map((day) => {
          const key = day.format("YYYY-MM-DD");
          const dayItems = byDateKey.get(key) ?? [];
          const inMonth = day.month() === focusMonth;
          const isToday = day.isSame(dayjs(), "day");
          const isDropTarget = dropDayKey === key && dragSourceId != null;
          return (
            <Box
              key={key}
              onDragOver={(event) => {
                if (!canDrag || dragSourceIdRef.current == null) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                if (dropDayKey !== key) setDropDayKey(key);
              }}
              onDragLeave={(event) => {
                const next = event.relatedTarget as Node | null;
                if (next && event.currentTarget.contains(next)) return;
                if (dropDayKey === key) setDropDayKey(null);
              }}
              onDrop={(event) => {
                event.preventDefault();
                suppressClickRef.current = true;
                const raw = event.dataTransfer.getData("text/plain");
                const parsed = Number.parseInt(raw, 10);
                const rfqId = Number.isFinite(parsed)
                  ? parsed
                  : dragSourceIdRef.current;
                dragSourceIdRef.current = null;
                setDragSourceId(null);
                setDropDayKey(null);
                if (rfqId == null || !onMoveRfq) return;
                const item = items.find((entry) => entry.id === rfqId);
                if (!item) return;
                if (itemDateKey(item, dateField) === key) return;
                void onMoveRfq(rfqId, key);
              }}
              sx={{
                minHeight: 120,
                border: `1px solid ${
                  isDropTarget ? blue[400] : isToday ? blue[400] : grey[300]
                }`,
                borderRadius: 1,
                bgcolor: isDropTarget
                  ? alpha(blue[100], 0.65)
                  : inMonth
                    ? grey[50]
                    : grey[100],
                opacity: inMonth ? 1 : 0.55,
                p: 0.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                overflow: "auto",
                outline: isDropTarget ? `2px dashed ${blue[400]}` : undefined,
                outlineOffset: isDropTarget ? -2 : undefined,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={isToday ? 700 : 600}
                color={isToday ? blue[700] : "text.secondary"}
              >
                {day.date()}
              </Typography>
              {dayItems.map((item) => {
                const c = getRfqStatusColor(item.rfqStatus, statusCatalog);
                const dueLabel = formatDisplayDate(item.dueDate);
                const isDragging = dragSourceId === item.id;
                const isLastVisited = highlightedRfqId === item.id;
                return (
                  <Box
                    key={item.id}
                    ref={isLastVisited ? highlightedItemRef : undefined}
                    draggable={canDrag}
                    aria-current={isLastVisited ? "true" : undefined}
                    title={isLastVisited ? "Last RFQ you opened" : undefined}
                    onDragStart={(event) => {
                      if (!canDrag) return;
                      event.stopPropagation();
                      suppressClickRef.current = true;
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", String(item.id));
                      dragSourceIdRef.current = item.id;
                      setDragSourceId(item.id);
                    }}
                    onDragEnd={() => {
                      dragSourceIdRef.current = null;
                      setDragSourceId(null);
                      setDropDayKey(null);
                    }}
                    onClick={() => {
                      if (suppressClickRef.current) {
                        suppressClickRef.current = false;
                        return;
                      }
                      onOpenRfq(item.id);
                    }}
                    sx={{
                      border: `1px solid ${isLastVisited ? lightBlue[400] : c.border}`,
                      borderLeft: `3px solid ${isLastVisited ? lightBlue[400] : c.border}`,
                      bgcolor: isLastVisited ? lightBlue[50] : "white",
                      borderRadius: 1,
                      p: 0.5,
                      cursor: canDrag ? "grab" : "pointer",
                      opacity: isDragging ? 0.45 : 1,
                      userSelect: "none",
                      boxShadow: isLastVisited
                        ? `inset 0 0 0 1px ${lightBlue[200]}`
                        : undefined,
                      "&:hover": {
                        bgcolor: isLastVisited ? lightBlue[100] : grey[100],
                      },
                      "&:active": canDrag ? { cursor: "grabbing" } : undefined,
                    }}
                  >
                    <Stack gap={0.25}>
                      <Stack direction="row" justifyContent="space-between" gap={0.5}>
                        <Typography
                          fontSize="0.72rem"
                          fontWeight={700}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.rfqNum}
                        </Typography>
                        <Stack direction="row" gap={0.25} flexShrink={0}>
                          {[item.rfqStatus, ...(item.extraStatuses ?? [])].map(
                            (status) => {
                              const chipColor = getRfqStatusColor(status, statusCatalog);
                              const chip = (
                                <Chip
                                  key={status}
                                  size="small"
                                  label={getRFQStatusLabel(status, statusCatalog)}
                                  sx={{
                                    height: 16,
                                    bgcolor: chipColor.bg,
                                    color: chipColor.fg,
                                    fontWeight: 600,
                                    "& .MuiChip-label": {
                                      px: 0.5,
                                      fontSize: "0.6rem",
                                    },
                                  }}
                                />
                              );
                              // Extra chips (e.g. Hot, PO) are flags, not tracked statuses.
                              return status === item.rfqStatus ? (
                                <RfqStatusSinceTooltip
                                  key={status}
                                  rfqId={item.id}
                                  status={status}
                                >
                                  {chip}
                                </RfqStatusSinceTooltip>
                              ) : (
                                chip
                              );
                            }
                          )}
                        </Stack>
                      </Stack>
                      <Typography
                        fontSize="0.68rem"
                        color="text.primary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.customerName || "—"}
                      </Typography>
                      <Typography
                        fontSize="0.65rem"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.buyerName || "—"}
                      </Typography>
                      {dueLabel ? (
                        <Typography fontSize="0.65rem" color={blue[800]} fontWeight={600}>
                          Due {dueLabel}
                        </Typography>
                      ) : null}
                      {item.note?.trim() ? (
                        <Typography
                          fontSize="0.62rem"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            whiteSpace: "normal",
                          }}
                        >
                          {item.note.trim()}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
};

export default WorkQueueCalendar;

/** Shared toggle control used by RfqsPage toolbar. */
export const WorkQueueViewToggle: React.FC<{
  value: "list" | "calendar";
  onChange: (value: "list" | "calendar") => void;
}> = ({ value, onChange }) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    size="small"
    onChange={(_e, next) => {
      if (next) onChange(next);
    }}
  >
    <ToggleButton value="list">List</ToggleButton>
    <ToggleButton value="calendar">Calendar</ToggleButton>
  </ToggleButtonGroup>
);

export const WorkQueueDateSortToggle: React.FC<{
  value: WorkQueueCalendarDateField;
  onChange: (value: WorkQueueCalendarDateField) => void;
}> = ({ value, onChange }) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    size="small"
    onChange={(_e, next: WorkQueueCalendarDateField | null) => {
      if (next) onChange(next);
    }}
  >
    <ToggleButton value="dueDate">Due Date</ToggleButton>
    <ToggleButton value="receivedDate">Received Date</ToggleButton>
  </ToggleButtonGroup>
);
